import type { OnboardingProgress, StepStatus } from "./onboarding-progress.js";

const prefix = "v1.";
/** 400 Tage — die Obergrenze, die Chrome für `Max-Age` noch akzeptiert. */
const maxAgeSeconds = 400 * 24 * 60 * 60;
const codes: Record<StepStatus, string> = { done: "d", skipped: "s" };

/**
 * Pro Account ein eigener Cookie, damit sich mehrere Konten im selben Browser
 * nicht gegenseitig als „schon gesehen“ markieren.
 */
export function onboardingCookieName(userId: string): string {
  return `cs2_tour_${userId.replace(/[^A-Za-z0-9_-]/g, "")}`;
}

/** `v1.id:d|id:s` — kompakt genug für alle Steps weit unter 4 KB. */
export function serializeProgress(progress: OnboardingProgress): string {
  const entries = Object.entries(progress).map(
    ([id, status]) => `${id}:${codes[status]}`,
  );
  return encodeURIComponent(prefix + entries.join("|"));
}

/** Kaputte oder fremde Werte ergeben leeren Fortschritt statt einer Exception. */
export function parseProgress(
  raw: string | null | undefined,
): OnboardingProgress {
  if (!raw) return {};
  let value: string;
  try {
    value = decodeURIComponent(raw);
  } catch {
    return {};
  }
  if (!value.startsWith(prefix)) return {};
  const progress: Record<string, StepStatus> = {};
  for (const entry of value.slice(prefix.length).split("|")) {
    const separator = entry.lastIndexOf(":");
    if (separator <= 0) continue;
    const id = entry.slice(0, separator);
    const code = entry.slice(separator + 1);
    if (code === codes.done) progress[id] = "done";
    else if (code === codes.skipped) progress[id] = "skipped";
  }
  return progress;
}

export function readProgress(userId: string): OnboardingProgress {
  try {
    const name = `${onboardingCookieName(userId)}=`;
    const match = document.cookie
      .split("; ")
      .find((cookie) => cookie.startsWith(name));
    return parseProgress(match?.slice(name.length));
  } catch {
    return {};
  }
}

export function writeProgress(
  userId: string,
  progress: OnboardingProgress,
): void {
  try {
    const secure = location.protocol === "https:" ? "; Secure" : "";
    document.cookie = `${onboardingCookieName(userId)}=${serializeProgress(progress)}; Path=/; Max-Age=${maxAgeSeconds}; SameSite=Lax${secure}`;
  } catch {
    // Ohne Cookies läuft die Tour trotzdem — sie erscheint dann erneut.
  }
}
